import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useExplorerConnectionLimit } from "./useExplorerConnectionLimit";
import {
  getExplorerLimitMessageFromError,
  getExplorerLimitReachedMessage,
} from "../utils/explorerConnectionLimitCopy";
import { isExplorerConnectionWeeklyLimitError } from "../utils/explorerConnectionLimitError";
import { ExplorerConnectionLimitModal } from "../components/Connections/ExplorerConnectionLimitModal";

const PAYWALL_SHOWN_SESSION_KEY = "smbf-explorer-connection-paywall-shown";

type Options = {
  /** Show paywall once per browser session when the weekly limit is already exhausted. */
  autoShowPaywall?: boolean;
};

/**
 * Shared Explorer hard paywall: modal + guards with the same weekResetsUtc as the limit API.
 */
export function useExplorerConnectionLimitFeedback(options?: Options) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [modalOpen, setModalOpen] = useState(false);
  const {
    isExplorer,
    canPerformConnectionAction,
    used,
    limit,
    weekResetsUtc,
    refetchLimit,
  } = useExplorerConnectionLimit();

  const openPaywall = useCallback(
    (opts?: { toast?: boolean; error?: unknown }) => {
      refetchLimit();
      if (opts?.toast !== false) {
        const message = opts?.error
          ? getExplorerLimitMessageFromError(t, opts.error, locale, {
              locale,
              weekResetsUtc,
              used,
              limit,
            })
          : getExplorerLimitReachedMessage(t, {
              locale,
              weekResetsUtc,
              used,
              limit,
            });
        toast.error(message);
      }
      setModalOpen(true);
    },
    [t, locale, weekResetsUtc, used, limit, refetchLimit]
  );

  const showLimitReached = useCallback(
    (error?: unknown) => {
      openPaywall({ toast: true, error });
    },
    [openPaywall]
  );

  const guardConnectionAction = useCallback(() => {
    if (canPerformConnectionAction) return false;
    openPaywall({ toast: false });
    return true;
  }, [canPerformConnectionAction, openPaywall]);

  const handleConnectionLimitError = useCallback(
    (error: unknown) => {
      if (isExplorerConnectionWeeklyLimitError(error)) {
        showLimitReached(error);
        return true;
      }
      return false;
    },
    [showLimitReached]
  );

  useEffect(() => {
    if (!options?.autoShowPaywall || !isExplorer || canPerformConnectionAction) {
      return;
    }
    if (sessionStorage.getItem(PAYWALL_SHOWN_SESSION_KEY) === "1") {
      return;
    }
    sessionStorage.setItem(PAYWALL_SHOWN_SESSION_KEY, "1");
    openPaywall({ toast: false });
  }, [
    options?.autoShowPaywall,
    isExplorer,
    canPerformConnectionAction,
    openPaywall,
  ]);

  const limitModal = (
    <ExplorerConnectionLimitModal
      show={modalOpen}
      onHide={() => setModalOpen(false)}
      weekResetsUtc={weekResetsUtc}
      used={used}
      limit={limit}
    />
  );

  return {
    isExplorer,
    canPerformConnectionAction,
    guardConnectionAction,
    openPaywall,
    handleConnectionLimitError,
    showLimitReached,
    limitModal,
  };
}
